/**
 * The prioritization memo, printed — same paper / Paged.js treatment as the
 * V2MOM and Strategy Canvas prints. "Save as PDF" is the browser's print dialog.
 *
 * Route: /prioritization/sessions/:sessionId/reports/:artifactId/print
 * Body: PrioritizationMemoBody, the same component the screen uses.
 */
import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import type { PrintPaperId } from '@/lib/print/paperPresets';
import {
  PRINT_PAPER_PRESETS,
  getPrintPaperPreset,
  loadSavedPrintPaperId,
  savePrintPaperId,
} from '@/lib/print/paperPresets';
import { PrintChrome } from '@/components/print/PrintChrome';
import { Select } from '@/components/ds';
import { fetchReportArtifact, type ReportArtifact } from '@/lib/reportGenerationService';
import { isPrioritizationMemo } from '@/lib/prioritizationMemo';
import { PrioritizationMemoBody } from '@/components/prioritization/memo/PrioritizationMemoBody';

export function MemoPrintPage() {
  const { sessionId, artifactId } = useParams<{ sessionId: string; artifactId: string }>();
  const [artifact, setArtifact] = useState<ReportArtifact | null>(null);
  const [loading, setLoading] = useState(true);
  const [paperId, setPaperId] = useState<PrintPaperId>(() => loadSavedPrintPaperId());

  useEffect(() => {
    savePrintPaperId(paperId);
  }, [paperId]);

  useEffect(() => {
    if (!artifactId) return;
    setLoading(true);
    fetchReportArtifact(artifactId)
      .then(setArtifact)
      .finally(() => setLoading(false));
  }, [artifactId]);

  const preset = useMemo(() => getPrintPaperPreset(paperId), [paperId]);
  const handlePaperChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setPaperId(e.target.value as PrintPaperId);
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center gap-2">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
        <span className="text-[var(--text-ter)]">Loading the memo…</span>
      </div>
    );
  }
  if (!artifact || !isPrioritizationMemo(artifact.content)) {
    return <div className="p-8 text-center text-sm text-[var(--text-sec)]">This memo was not found.</div>;
  }

  const memo = artifact.content;
  return (
    <PrintChrome
      documentTitle={`Prioritization memo — ${memo.session.name}`}
      pageSizeCss={preset.pageSizeCss}
      previewMaxWidthMm={preset.previewMaxWidthMm}
      backHref={`/prioritization/sessions/${sessionId}/reports/${artifact.id}`}
      backLabel="Return to the memo"
      pagedRenderKey={[artifact.id, paperId].join('|')}
      toolbarAside={
        <label className="flex flex-wrap items-center gap-2 text-xs text-[var(--text-sec)] print:hidden">
          <span>Paper size</span>
          <Select
            size="sm"
            value={paperId}
            onChange={handlePaperChange}
            options={PRINT_PAPER_PRESETS.map((p) => ({ value: p.id, label: p.label }))}
          />
        </label>
      }
    >
      <PrioritizationMemoBody memo={memo} generatedAt={artifact.generatedAt} />
    </PrintChrome>
  );
}

export default MemoPrintPage;
